import React, { useState, useEffect, useMemo, useRef } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Button, 
  Modal, 
  TextField, 
  IconButton, 
  Grid,
  CircularProgress,
  Alert,
  styled,
  TablePagination,
  InputAdornment,
  Stack,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  FormHelperText,
  Autocomplete,
  Tooltip,
  ButtonGroup,
  Card,
  CardContent,
  Chip,
  Divider,
  useMediaQuery,
  useTheme,
  Menu,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import LocalAtmIcon from '@mui/icons-material/LocalAtm';
import AssessmentIcon from '@mui/icons-material/Assessment';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import FilterListIcon from '@mui/icons-material/FilterList';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import ownAuthSession from '../../../services/auth/ownAuthSession';
import firebaseDataService from '../../../services/FirebaseDataService';
import { getCurrencySettings } from '../../../services/migration/companyDataService';
import { useAuth } from '../../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import DepositModal from './DepositModal';
import WithdrawModal from './WithdrawModal';
import CashTransferModal from './CashTransferModal';
import CashboxTransferModal from './CashboxTransferModal';
import CashboxBankTransferModal from './CashboxBankTransferModal';
import CashExpenseModal, { type ExpenseCashboxOption } from './CashExpenseModal';
import CashExpenseCategoriesModal from './CashExpenseCategoriesModal';
import CashboxTransactions from './CashboxTransactions';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import * as ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { ACCOUNTING_API } from '../../../config/api';
import { accountingApi } from '../../../config/apiClient';
import AccountingDataTable from '../shared/AccountingDataTable';
import type { DataTableColumn } from '../shared/AccountingDataTable';
import { StatsCardItem, StatsCardsContainer, STATS_COLORS, formatStatsCurrency } from '../shared/StatsCard';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import { useTranslation } from 'react-i18next';

// Kaydırılabilir ana içerik için styled component
const ScrollableContent = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  overflowY: 'auto',
  height: 'calc(100vh - 64px)', // Header yüksekliğini çıkarıyoruz
  '&::-webkit-scrollbar': {
    width: '8px',
  },
  '&::-webkit-scrollbar-track': {
    background: '#f1f1f1',
    borderRadius: '4px',
  },
  '&::-webkit-scrollbar-thumb': {
    background: '#888',
    borderRadius: '4px',
  },
  '&::-webkit-scrollbar-thumb:hover': {
    background: '#555',
  },
}));

// Başlık alanı için styled component
const HeaderContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: theme.spacing(3),
}));

// Kasa arayüzü
interface CashboxItem {
  id: string;
  name: string;
  description: string;
  currency: string;
  balance: number;
  createdAt: string;
  isActive: boolean;
  branchId?: string | null;
  isDefault?: boolean;
  hasTransactions?: boolean;
}

// Şube arayüzü
interface Branch {
  id: string;
  name: string;
}

interface CashboxProps {
  isManager?: boolean;
  managerBranchId?: string | null;
  /** cash_expense yetkisi (yalnızca manager modunda anlamlı; admin her zaman ekleyebilir) */
  canAddExpense?: boolean;
  /** cash_movements yetkisi: manager kendi şube kasalarının hareket raporunu panel içinde açar */
  canViewMovements?: boolean;
}

const Cashbox: React.FC<CashboxProps> = ({ isManager = false, managerBranchId = null, canAddExpense = false, canViewMovements = false }) => {
  const showExpenseButton = !isManager || canAddExpense;
  const [isExpenseModalOpen, setIsExpenseModalOpen] = useState(false);
  const [showManagerMovements, setShowManagerMovements] = useState(false);
  const { t, i18n } = useTranslation('accounting');
  // ✅ Merkezi Cache Sistemi - AuthContext'ten al
  const { branches } = useAuth();
  
  // ✅ Manager modu: sadece kendi şubesine ait kasalar
  const applyManagerFilter = (list: CashboxItem[]): CashboxItem[] =>
    (isManager && managerBranchId) ? list.filter(c => c.branchId === managerBranchId) : list;
  
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const locale = i18n.language?.toLowerCase().startsWith('en') ? 'en-US' : 'tr-TR';
  const [open, setOpen] = useState(false);
  const [kasaAdi, setKasaAdi] = useState('');
  const [aciklama, setAciklama] = useState('');
  const [selectedCurrency, setSelectedCurrency] = useState('TRY');
  const [selectedBranch, setSelectedBranch] = useState<Branch | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [cashboxes, setCashboxes] = useState<CashboxItem[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredCashboxes, setFilteredCashboxes] = useState<CashboxItem[]>([]);
  const [companyId, setCompanyId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  
  // Para birimleri
  const [currencies, setCurrencies] = useState<string[]>(['TRY', 'USD', 'EUR', 'GBP']);
  
  // Sayfalama için state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  // Düzenleme ve silme modalları için state'ler
  const [selectedCashbox, setSelectedCashbox] = useState<CashboxItem | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  
  // Düzenleme state'leri
  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editCurrency, setEditCurrency] = useState('');
  const [editBranch, setEditBranch] = useState<Branch | null>(null);
  const [updating, setUpdating] = useState(false);
  
  // Hareketler modalı için state'ler
  const [isTransactionsModalOpen, setIsTransactionsModalOpen] = useState(false);
  const [transactionCashbox, setTransactionCashbox] = useState<CashboxItem | null>(null);

  // Para yatırma modalı için state
  const [isDepositModalOpen, setIsDepositModalOpen] = useState(false);
  const [depositCashbox, setDepositCashbox] = useState<CashboxItem | null>(null);

  // Para çekme modalı için state
  const [isWithdrawModalOpen, setIsWithdrawModalOpen] = useState(false);
  const [withdrawCashbox, setWithdrawCashbox] = useState<CashboxItem | null>(null);

  // Para transferi modalı için state'ler
  const [isTransferModalOpen, setIsTransferModalOpen] = useState(false);

  // Kasa transferi modalı için state (yeni - kasadan kasaya)
  const [isCashboxTransferModalOpen, setIsCashboxTransferModalOpen] = useState(false);
  const [transferSourceCashbox, setTransferSourceCashbox] = useState<CashboxItem | null>(null);

  // Banka transferi modalı için state (yeni - kasadan bankaya)
  const [isBankTransferModalOpen, setIsBankTransferModalOpen] = useState(false);
  const [bankTransferSourceCashbox, setBankTransferSourceCashbox] = useState<CashboxItem | null>(null);

  // Excel indirme için state
  const [loadingExport, setLoadingExport] = useState(false);

  // "3 nokta" menü için state
  const [anchorElMore, setAnchorElMore] = useState<null | HTMLElement>(null);
  const [expenseCategoriesOpen, setExpenseCategoriesOpen] = useState(false);
  
  // Kasa işlemler menüsü için state
  const [actionMenuAnchor, setActionMenuAnchor] = useState<null | HTMLElement>(null);
  const [selectedCashboxForMenu, setSelectedCashboxForMenu] = useState<CashboxItem | null>(null);
  
  // Filtreleri göster/gizle için state (varsayılan: gizli)
  const [showFilters, setShowFilters] = useState(false);
  
  // Şube filtresi için state
  const [selectedBranchFilter, setSelectedBranchFilter] = useState<Branch | null>(null);
  
  // Kasa özet (dashboard) verileri
  const [cashboxSummary, setCashboxSummary] = useState<{
    currentBalance: number;
    periodIncome: number;
    periodExpense: number;
    periodNet: number;
  } | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(true);
  
  const handleOpen = () => {
    // ✅ Manager modu: şube otomatik seçilir ve kilitlenir
    if (isManager && managerBranchId) {
      const branch = branches.find(b => b.id === managerBranchId);
      setSelectedBranch(branch || null);
    }
    setOpen(true);
  };
  const handleClose = () => {
    setOpen(false);
    setKasaAdi('');
    setAciklama('');
    setSelectedCurrency('TRY');
    setSelectedBranch(null);
  };

  // "3 nokta" menü fonksiyonları
  const handleOpenMoreMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElMore(event.currentTarget);
  };

  const handleCloseMoreMenu = () => {
    setAnchorElMore(null);
  };

  // Kasa işlemler menüsünü aç
  const handleOpenActionMenu = (event: React.MouseEvent<HTMLElement>, cashbox: CashboxItem) => {
    event.stopPropagation();
    setActionMenuAnchor(event.currentTarget);
    setSelectedCashboxForMenu(cashbox);
  };

  // Kasa işlemler menüsünü kapat
  const handleCloseActionMenu = () => {
    setActionMenuAnchor(null);
    setSelectedCashboxForMenu(null);
  };

  // Menüden düzenle
  const handleMenuEdit = () => {
    if (selectedCashboxForMenu && !isManager) {
      handleOpenEditModal(selectedCashboxForMenu);
    }
    handleCloseActionMenu();
  };

  // Menüden sil
  const handleMenuDelete = () => {
    if (selectedCashboxForMenu) {
      handleOpenDeleteModal(selectedCashboxForMenu);
    }
    handleCloseActionMenu();
  };

  // Menüden kasa transferi
  const handleMenuTransfer = () => {
    if (selectedCashboxForMenu) {
      setTransferSourceCashbox(selectedCashboxForMenu);
      setIsCashboxTransferModalOpen(true);
    }
    handleCloseActionMenu();
  };

  // Menüden banka transferi
  const handleMenuBankTransfer = () => {
    if (selectedCashboxForMenu) {
      setBankTransferSourceCashbox(selectedCashboxForMenu);
      setIsBankTransferModalOpen(true);
    }
    handleCloseActionMenu();
  };

  // Kasa transferi modalını kapat
  const handleCloseCashboxTransferModal = () => {
    setIsCashboxTransferModalOpen(false);
    setTransferSourceCashbox(null);
    // Kasaları yeniden yükle
    reloadCashboxes();
  };

  // Banka transferi modalını kapat
  const handleCloseBankTransferModal = () => {
    setIsBankTransferModalOpen(false);
    setBankTransferSourceCashbox(null);
    // Kasaları yeniden yükle
    reloadCashboxes();
  };

  const handleOpenReports = () => {
    handleCloseMoreMenu();
    if (isManager) {
      setShowManagerMovements(true);
      return;
    }
    navigate('/accounting/cashbox/transactions');
  };

  const handleCurrencyChange = (event: SelectChangeEvent) => {
    setSelectedCurrency(event.target.value);
  };

  const handleBranchChange = (_event: React.SyntheticEvent, newValue: Branch | null) => {
    setSelectedBranch(newValue);
  };

  const handleSave = async () => {
    if (!companyId || !kasaAdi.trim()) return;
    
    setSaving(true);
    try {
      console.log('📥 Yeni kasa oluşturuluyor (SQL)...');
      
      // Yeni kasa verisi (SQL için)
      const newCashbox = {
        companyId: companyId,
        name: kasaAdi.trim(),
        description: aciklama.trim(),
        currency: selectedCurrency,
        branchId: selectedBranch?.id || null,
        isDefault: false, // Yeni kasalar varsayılan değil
        createdBy: ownAuthSession.getCurrentEmail() || 'System'
      };
      
      console.log('📤 Gönderilen veri:', newCashbox);
      
      // SQL API'ye kaydet
      const response = await accountingApi.post(ACCOUNTING_API.CASHBOXES, newCashbox);
      const result = response.data;
      console.log('✅ Kasa oluşturuldu (SQL):', result);
      
      handleClose();
      setError(null);
    } catch (error: any) {
      console.error('❌ Kasa kaydedilirken hata:', error);
      setError(error.response?.data?.message || (error instanceof Error ? error.message : 'Kasa kaydedilirken bir hata oluştu.'));
    } finally {
      setSaving(false);
    }
  };

  // Para birimlerini yükleme (Cloud Functions)
  const loadCurrencies = async (companyId: string) => {
    try {
      const currencyData = await getCurrencySettings(companyId);
      
      // Varsayılan para birimi listesi
      const currencyList: string[] = ['TRY']; // TRY her zaman var
      
      if (currencyData) {
        
        // Veritabanından gelen para birimleri
        Object.entries(currencyData).forEach(([code, active]: [string, any]) => {
          // TRY zaten eklendiği için tekrar eklemeye gerek yok
          if (code !== 'TRY' && active === true) {
            currencyList.push(code);
          }
        });
      }
      
      setCurrencies(currencyList);
      
      // Eğer seçili para birimi artık mevcut değilse, TRY'ye ayarla
      if (!currencyList.includes(selectedCurrency)) {
        setSelectedCurrency('TRY');
      }
    } catch (error) {
      console.error('Para birimleri yüklenirken hata:', error);
      // Hata durumunda varsayılan para birimleri
      setCurrencies(['TRY', 'USD', 'EUR', 'GBP']);
    }
  };

  // Türk formatından sayıya çevirme (örn: "66.000,00" → 66000.00)
  const parseTurkishNumber = (value: any): number => {
    if (typeof value === 'number') return value;
    if (!value) return 0;
    
    // String'e çevir
    const strValue = String(value);
    
    // Nokta (bin ayracı) kaldır, virgül (ondalık) nokta yap
    const cleanValue = strValue.replace(/\./g, '').replace(',', '.');
    return parseFloat(cleanValue) || 0;
  };

  // Para birimi sembolünü döndür
  const getCurrencySymbol = (currency: string): string => {
    switch(currency) {
      case 'TRY': return '₺';
      case 'USD': return '$';
      case 'EUR': return '€';
      case 'GBP': return '£';
      case 'JPY': return '¥';
      case 'CHF': return 'Fr.';
      case 'AUD': return 'A$';
      case 'CAD': return 'C$';
      case 'CNY': return '¥';
      default: return currency;
    }
  };

  // Düzenleme modalını aç
  const handleOpenEditModal = (cashbox: CashboxItem) => {
    // Varsayılan kasa kontrolü
    if (cashbox.isDefault) {
      setError('Varsayılan kasa (Genel Kasa) düzenlenemez.');
      return;
    }
    
    // Sadece hareket geçmişi kontrolü yapılıyor, bakiye kontrolü kaldırıldı
    if (cashbox.hasTransactions) {
      setError(`Hareket geçmişi bulunan kasa düzenlenemez. Kasa: ${cashbox.name}`);
      return;
    }
    
    setSelectedCashbox(cashbox);
    setEditName(cashbox.name);
    setEditDescription(cashbox.description || '');
    setEditCurrency(cashbox.currency);
    
    // Şube bul
    if (cashbox.branchId) {
      const branch = branches.find(b => b.id === cashbox.branchId);
      setEditBranch(branch || null);
    } else {
      setEditBranch(null);
    }
    
    setIsEditModalOpen(true);
  };

  // Düzenleme modalını kapat
  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
    setSelectedCashbox(null);
  };

  // Düzenlemeyi kaydet
  const handleUpdateCashbox = async () => {
    if (!selectedCashbox || !companyId || !editName.trim()) return;
    
    setUpdating(true);
    try {
      console.log('🔄 Kasa güncelleniyor (SQL ID:', selectedCashbox.id, ')');
      
      // Account ID'yi sayıya çevir (SQL ID)
      const numericId = parseInt(selectedCashbox.id, 10);
      if (isNaN(numericId)) {
        throw new Error('Geçersiz kasa ID formatı');
      }
      
      // Güncellenecek kasa verisi
      const updatedCashbox = {
        id: numericId,
        companyId: companyId,
        name: editName.trim(),
        description: editDescription.trim(),
        currency: editCurrency,
        branchId: editBranch?.id || null,
        isActive: selectedCashbox.isActive,
        isDefault: selectedCashbox.isDefault,
        updatedBy: ownAuthSession.getCurrentEmail() || 'System'
      };
      
      console.log('📤 Gönderilen veri:', updatedCashbox);
      
      // SQL API'ye güncelleme isteği gönder
      const response = await accountingApi.post(ACCOUNTING_API.CASHBOX_UPDATE(numericId), updatedCashbox);
      const result = response.data;
      console.log('✅ Kasa güncellendi (SQL):', result);
      
      // Kasaları yeniden yükle
      try {
        const listResponse = await accountingApi.get(ACCOUNTING_API.CASHBOX_BY_COMPANY);
        const sqlCashboxes = listResponse.data.data || listResponse.data;
        
        let cashboxList: CashboxItem[] = sqlCashboxes.map((data: any) => ({
          id: data.id.toString(),
          name: data.name || '',
          description: data.description || '',
          currency: data.currency || 'TRY',
          balance: parseTurkishNumber(data.balance),
          createdAt: data.createdAt || '',
          isActive: data.isActive !== false,
          branchId: data.branchId || null,
          isDefault: data.isDefault || false,
          hasTransactions: data.hasTransactions || false
        }));
        
        cashboxList.sort((a, b) => {
          if (a.isDefault) return -1;
          if (b.isDefault) return 1;
          return a.name.localeCompare(b.name);
        });
        
        setCashboxes(cashboxList);
        setFilteredCashboxes(cashboxList);
      } catch (reloadError) {
        console.error('❌ Kasalar yeniden yüklenirken hata:', reloadError);
      }
      
      handleCloseEditModal();
      setError(null);
    } catch (error: any) {
      console.error('❌ Kasa güncellenirken hata:', error);
      setError(error.response?.data?.message || (error instanceof Error ? error.message : 'Kasa güncellenirken bir hata oluştu.'));
    } finally {
      setUpdating(false);
    }
  };

  // Silme modalını aç
  const handleOpenDeleteModal = (cashbox: CashboxItem) => {
    // Varsayılan kasa kontrolü
    if (cashbox.isDefault) {
      setError('Varsayılan kasa (Genel Kasa) silinemez.');
      return;
    }
    
    // Sadece hareket geçmişi kontrolü yapılıyor, bakiye kontrolü kaldırıldı
    if (cashbox.hasTransactions) {
      setError(`Hareket geçmişi bulunan kasa silinemez. Kasa: ${cashbox.name}`);
      return;
    }
    
    setSelectedCashbox(cashbox);
    setIsDeleteModalOpen(true);
  };

  // Silme modalını kapat
  const handleCloseDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setSelectedCashbox(null);
  };

  // Kasayı sil
  const handleDeleteCashbox = async () => {
    if (!selectedCashbox || !companyId) return;
    
    setDeleting(true);
    try {
      console.log('🗑️ Kasa siliniyor (SQL ID:', selectedCashbox.id, ')');
      
      // Account ID'yi sayıya çevir (SQL ID)
      const numericId = parseInt(selectedCashbox.id, 10);
      if (isNaN(numericId)) {
        throw new Error('Geçersiz kasa ID formatı');
      }
      
      // SQL API'ye silme isteği gönder
      await accountingApi.post(ACCOUNTING_API.CASHBOX_DELETE(numericId));

      console.log('✅ Kasa silindi (SQL)');
      
      // Kasaları yeniden yükle
      try {
        const listResponse = await accountingApi.get(ACCOUNTING_API.CASHBOX_BY_COMPANY);
        const sqlCashboxes = listResponse.data.data || listResponse.data;
        
        console.log('📋 [DELETE] Backend\'den gelen kasalar:', sqlCashboxes.length, 'adet');
        console.log('📋 [DELETE] Kasalar:', sqlCashboxes.map((c: any) => ({ id: c.id, name: c.name, isDeleted: c.isDeleted })));
        
        let cashboxList: CashboxItem[] = sqlCashboxes.map((data: any) => ({
          id: data.id.toString(),
          name: data.name || '',
          description: data.description || '',
          currency: data.currency || 'TRY',
          balance: parseTurkishNumber(data.balance),
          createdAt: data.createdAt || '',
          isActive: data.isActive !== false,
          branchId: data.branchId || null,
          isDefault: data.isDefault || false,
          hasTransactions: data.hasTransactions || false
        }));
        
        cashboxList.sort((a, b) => {
          if (a.isDefault) return -1;
          if (b.isDefault) return 1;
          return a.name.localeCompare(b.name);
        });
        
        console.log('📋 [DELETE] State\'e kaydedilen kasalar:', cashboxList.length, 'adet');
        console.log('📋 [DELETE] Kaydedilen kasalar:', cashboxList.map(c => ({ id: c.id, name: c.name })));
        
        setCashboxes(cashboxList);
        setFilteredCashboxes(cashboxList);
      } catch (reloadError) {
        console.error('❌ [DELETE] Kasalar yeniden yüklenirken hata:', reloadError);
      }
      
      handleCloseDeleteModal();
      setError(null);
    } catch (error: any) {
      console.error('❌ Kasa silinirken hata:', error);
      setError(error.response?.data?.message || (error instanceof Error ? error.message : 'Kasa silinirken bir hata oluştu.'));
    } finally {
      setDeleting(false);
    }
  };

  // Hareketler modalını aç
  const handleOpenTransactionsModal = (cashbox: CashboxItem) => {
    setTransactionCashbox(cashbox);
    setIsTransactionsModalOpen(true);
  };
  
  // Hareketler modalını kapat
  const handleCloseTransactionsModal = () => {
    setIsTransactionsModalOpen(false);
    setTransactionCashbox(null);
  };

  // Para yatırma modalını aç
  const handleOpenDepositModal = (cashbox: CashboxItem) => {
    setDepositCashbox(cashbox);
    setIsDepositModalOpen(true);
    setIsTransactionsModalOpen(false); // Hareketler modalını kapat
  };
  
  // Para yatırma modalını kapat
  const handleCloseDepositModal = () => {
    setIsDepositModalOpen(false);
    setDepositCashbox(null);
    // Kasaları yeniden yükle
    reloadCashboxes();
  };

  // Para çekme modalını aç
  const handleOpenWithdrawModal = (cashbox: CashboxItem) => {
    setWithdrawCashbox(cashbox);
    setIsWithdrawModalOpen(true);
    setIsTransactionsModalOpen(false); // Hareketler modalını kapat
  };

  // Para çekme modalını kapat
  const handleCloseWithdrawModal = () => {
    setIsWithdrawModalOpen(false);
    setWithdrawCashbox(null);
    // Kasaları yeniden yükle
    reloadCashboxes();
  };

  // Para transferi modalını aç
  const handleOpenTransferModal = () => {
    setIsTransferModalOpen(true);
    // Eğer hareketler modalı açıksa, kapat
    setIsTransactionsModalOpen(false); 
  };
  
  // Para transferi modalını kapat
  const handleCloseTransferModal = () => {
    setIsTransferModalOpen(false);
  };

  // Excel'e aktarma fonksiyonu
  const exportToExcel = async () => {
    try {
      setLoadingExport(true);
      
      // Excel workbook oluştur
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet(t('cash.excel.sheetName'));
      
      // Sütunları tanımla
      worksheet.columns = [
        { header: t('cash.excel.columns.cashName'), key: 'name', width: 25 },
        { header: t('cash.excel.columns.description'), key: 'description', width: 30 },
        { header: t('cash.excel.columns.currency'), key: 'currency', width: 15 },
        { header: t('cash.excel.columns.balance'), key: 'balance', width: 20 },
        { header: t('cash.excel.columns.branch'), key: 'branch', width: 25 },
        { header: t('cash.excel.columns.createdAt'), key: 'createdAt', width: 20 },
        { header: t('cash.excel.columns.status'), key: 'isActive', width: 15 },
      ];
      
      // Stil tanımlamaları
      const headerStyle = {
        font: { bold: true, color: { argb: 'FFFFFFFF' } },
        fill: { type: 'pattern' as const, pattern: 'solid' as const, fgColor: { argb: 'FF4F81BD' } },
        border: {
          top: { style: 'thin' as const },
          left: { style: 'thin' as const },
          bottom: { style: 'thin' as const },
          right: { style: 'thin' as const }
        }
      };
      
      // Başlık stilini uygula
      worksheet.getRow(1).eachCell(cell => {
        cell.style = headerStyle;
      });
      
      // Filtrelenmiş verileri ekle
      filteredCashboxes.forEach(cashbox => {
        worksheet.addRow({
          name: cashbox.name,
          description: cashbox.description,
          currency: `${getCurrencySymbol(cashbox.currency)} ${cashbox.currency}`,
          balance: `${cashbox.balance.toLocaleString(locale)} ${getCurrencySymbol(cashbox.currency)}`,
          branch: getBranchName(cashbox.branchId),
          createdAt: formatDate(cashbox.createdAt),
          isActive: cashbox.isActive ? t('cash.status.active') : t('cash.status.inactive')
        });
      });
      
      // Zebrastripe (alternatif satır renklendirme)
      worksheet.eachRow((row, rowNumber) => {
        if (rowNumber > 1) {
          const fillColor = rowNumber % 2 === 0 ? 'FFF2F2F2' : 'FFFFFFFF';
          row.eachCell(cell => {
            cell.style = {
              fill: { type: 'pattern' as const, pattern: 'solid' as const, fgColor: { argb: fillColor } },
              border: {
                top: { style: 'thin' as const },
                left: { style: 'thin' as const },
                bottom: { style: 'thin' as const },
                right: { style: 'thin' as const }
              }
            };
          });
        }
      });
      
      // Dosya adına tarih ekle
      const currentDate = new Date().toISOString().slice(0, 10).replace(/-/g, '');
      const fileName = `${t('cash.excel.filePrefix')}_${currentDate}.xlsx`;
      
      // Excel dosyasını oluştur ve indir
      const buffer = await workbook.xlsx.writeBuffer();
      saveAs(new Blob([buffer]), fileName);
      
      setError(null); // Varsa hata mesajını temizle
      console.log(`${filteredCashboxes.length} adet kasa Excel dosyasına aktarıldı.`);
      
    } catch (error) {
      console.error('Excel dışa aktarma hatası:', error);
      setError(t('cash.errors.exportFailed'));
    } finally {
      setLoadingExport(false);
    }
  };
  
  // Tarih formatla
  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return new Intl.DateTimeFormat(locale, {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      }).format(date);
    } catch (error) {
      return dateString;
    }
  };

  const cashboxLoadedRef = useRef(false);

  useEffect(() => {
    if (cashboxLoadedRef.current) return;
    cashboxLoadedRef.current = true;

    const loadCashboxes = async () => {
      try {
        setLoading(true);
        
        const uid = ownAuthSession.getCurrentUid();
        if (!uid) {
          setError('Kullanıcı oturumu açık değil.');
          setLoading(false);
          return;
        }
        
        // Kullanıcının şirket ID'sini al (Cloud Functions)
        const userData = await firebaseDataService.getUserData(uid);
        
        if (!userData) {
          setError('Kullanıcı bilgileri bulunamadı.');
          setLoading(false);
          return;
        }
        
        const companyId = userData.companyId;
        setCompanyId(companyId || null);
        
        if (!companyId) {
          setError('Şirket bilgisi bulunamadı.');
          setLoading(false);
          return;
        }
        
        // Para birimleri yükle (hala Firebase'den)
        loadCurrencies(companyId);
        
        console.log('📋 Kasalar SQL\'den yükleniyor...');
        
        // SQL API'den kasaları yükle
        const response = await accountingApi.get(ACCOUNTING_API.CASHBOX_BY_COMPANY);
        const sqlCashboxes = response.data.data || response.data;
        
        console.log('✅ SQL\'den yüklenen kasalar (hesaplanmış bakiyeler ile):', sqlCashboxes);
        
        // ✅ Artık backend'de bakiyeler hesaplanmış olarak geliyor!
        // Ayrıca /balances endpoint'ine call yapmaya gerek yok
        
        // SQL verilerini frontend formatına çevir
        let cashboxList: CashboxItem[] = sqlCashboxes.map((data: any) => ({
          id: data.id.toString(), // SQL ID'yi string'e çevir
          name: data.name || '',
          description: data.description || '',
          currency: data.currency || 'TRY',
          balance: parseTurkishNumber(data.balance), // ✅ Backend'de hesaplanmış bakiye
          createdAt: data.createdAt || '',
          isActive: data.isActive !== false,
          branchId: data.branchId || null,
          isDefault: data.isDefault || false,
          hasTransactions: data.hasTransactions || false
        }));
        
        // Genel Kasa kontrolü
        const hasGeneralCashbox = cashboxList.some(cashbox => cashbox.isDefault);
        
        // Eğer Genel Kasa yoksa oluştur (manager modunda oluşturulmaz)
        if (!hasGeneralCashbox && !isManager) {
          try {
            console.log('⚠️ Genel Kasa bulunamadı, oluşturuluyor...');
            
            const generalCashbox = {
              companyId: companyId,
              name: "Genel Kasa",
              description: "Varsayılan şirket kasası",
              currency: "TRY",
              isDefault: true,
              createdBy: ownAuthSession.getCurrentEmail() || 'System'
            };
            
            // Genel Kasa'yı SQL'e kaydet
            const createResponse = await accountingApi.post(ACCOUNTING_API.CASHBOXES, generalCashbox);
            const createResult = createResponse.data;
            console.log('✅ Genel Kasa otomatik oluşturuldu:', createResult);
            
            cashboxList.unshift({
              id: createResult.data.id.toString(),
              name: createResult.data.name,
              description: createResult.data.description,
              currency: createResult.data.currency,
              balance: 0,
              createdAt: createResult.data.createdAt,
              isActive: true,
              branchId: null,
              isDefault: true,
              hasTransactions: false
            });
          } catch (error) {
            console.error('❌ Genel Kasa oluşturulurken hata:', error);
            setError('Varsayılan kasa oluşturulurken bir hata oluştu.');
          }
        }
        
        // Genel Kasa'yı en başa al, diğerlerini isme göre sırala
        cashboxList.sort((a, b) => {
          if (a.isDefault) return -1;
          if (b.isDefault) return 1;
          return a.name.localeCompare(b.name);
        });
        
        console.log('✅ Kasalar yüklendi:', cashboxList.length, 'adet');
        console.log('💰 Bakiyeler:', cashboxList.map(c => ({ name: c.name, balance: c.balance })));
        
        setCashboxes(cashboxList);
        setFilteredCashboxes(applyManagerFilter(cashboxList));
        setLoading(false);
        
        // Kasa özet bilgilerini yükle (manager modunda şirket geneli özet gösterilmez)
        if (!isManager) {
          loadCashboxSummary(companyId);
        }
        
      } catch (error) {
        console.error('❌ Kasalar yüklenirken hata:', error);
        setError('Kasalar yüklenirken bir hata oluştu.');
        setLoading(false);
      }
    };
    
    loadCashboxes();
    
    // Cleanup - Artık Firebase listener yok
    return () => {
      // Cleanup işlemi yok
    };
  }, []);

  // Arama ve filtreleme fonksiyonu
  useEffect(() => {
    let filtered = applyManagerFilter(cashboxes);
    
    // Metin araması
    if (searchTerm.trim() !== '') {
      const lowerCaseSearchTerm = searchTerm.toLowerCase();
      filtered = filtered.filter(cashbox => 
        cashbox.name.toLowerCase().includes(lowerCaseSearchTerm) ||
        cashbox.description.toLowerCase().includes(lowerCaseSearchTerm)
      );
    }
    
    // Şube filtresi
    if (selectedBranchFilter) {
      filtered = filtered.filter(cashbox => cashbox.branchId === selectedBranchFilter.id);
    }
    
    setFilteredCashboxes(filtered);
    setPage(0); // Filtre değiştiğinde ilk sayfaya dön
  }, [searchTerm, cashboxes, selectedBranchFilter]);
  
  // Sayfalandırılmış kasalar
  const paginatedCashboxes = useMemo(() => {
    const startIndex = page * rowsPerPage;
    return filteredCashboxes.slice(startIndex, startIndex + rowsPerPage);
  }, [filteredCashboxes, page, rowsPerPage]);
  
  // Sayfa değişimi
  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number,
  ) => {
    setPage(newPage);
  };

  // Sayfa başına satır sayısı değişimi
  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Şube adını bul
  const getBranchName = (branchId: string | null | undefined): string => {
    if (!branchId) return '-';
    const branch = branches.find(b => b.id === branchId);
    return branch ? branch.name : '-';
  };

  // AccountingDataTable kolon tanımları
  const cashboxColumns = useMemo<DataTableColumn<CashboxItem>[]>(() => [
    {
      id: 'rowNumber',
      label: t('cash.table.columns.row'),
      width: '60px',
      render: (_row, index) => (
        <Box component="span" sx={{ color: 'text.secondary' }}>{index + 1}</Box>
      ),
    },
    {
      id: 'name',
      label: t('cash.table.columns.cashName'),
      field: 'name',
      sortable: true,
    },
    {
      id: 'branchId',
      label: t('cash.table.columns.branch'),
      sortable: true,
      render: (row) => <>{getBranchName(row.branchId)}</>,
    },
    {
      id: 'description',
      label: t('cash.table.columns.description'),
      field: 'description',
      sortable: true,
    },
    {
      id: 'currency',
      label: t('cash.table.columns.currency'),
      field: 'currency',
      sortable: true,
      render: (row) => <>{getCurrencySymbol(row.currency)} {row.currency}</>,
    },
    {
      id: 'balance',
      label: t('cash.table.columns.balance'),
      field: 'balance',
      align: 'right',
      sortable: true,
      render: (row) => (
        // Banka bakiyelerindeki stil ile birebir uyumlu görünmesi için Typography kullanıldı.
        // Yapı bozulmasın diye sadece sunum değiştirildi (variant, fontWeight, color, sağa hizalama).
        <Typography
          variant="body2"
          fontWeight="bold"
          color={row.balance >= 0 ? 'success.main' : 'error.main'}
          sx={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}
        >
          {row.balance.toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {getCurrencySymbol(row.currency)}
        </Typography>
      ),
    },
    // Şube yöneticisinde kasa üzerinde yapılabilecek işlem yok (düzenleme/silme/transfer kapalı)
    ...(isManager ? [] : [{
      id: 'actions',
      label: t('cash.table.columns.actions'),
      align: 'right' as const,
      render: (row: CashboxItem) => (
        <Stack direction="row" spacing={1} justifyContent="flex-end">
          <Button size="small" color="info" variant="outlined" onClick={(e) => { e.stopPropagation(); handleOpenTransactionsModal(row); }}>
            {t('cash.actions.process')}
          </Button>
          <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleOpenActionMenu(e, row); }}>
            <MoreVertIcon fontSize="small" />
          </IconButton>
        </Stack>
      ),
    }]),
  ], [branches, locale, t, isManager]);

  // Kasaları yeniden yükle (helper fonksiyon)
  // Kasa günlük özet bilgilerini yükle
  const loadCashboxSummary = async (compId: string) => {
    try {
      setSummaryLoading(true);
      const response = await accountingApi.get(ACCOUNTING_API.CASHBOX_DAILY_SUMMARY);
      const data = response.data.data || response.data;
      
      setCashboxSummary({
        currentBalance: data.currentBalance || 0,
        periodIncome: data.period?.income || 0,
        periodExpense: data.period?.expense || 0,
        periodNet: data.period?.total || 0,
      });
    } catch (error) {
      console.error('❌ Kasa özeti yüklenirken hata:', error);
    } finally {
      setSummaryLoading(false);
    }
  };

  const reloadCashboxes = async () => {
    if (!companyId) return;
    
    try {
      const response = await accountingApi.get(ACCOUNTING_API.CASHBOX_BY_COMPANY);
      const sqlCashboxes = response.data.data || response.data;
      
      console.log('🔄 [RELOAD] SQL\'den yüklenen kasalar (hesaplanmış bakiyeler ile):', sqlCashboxes);
      
      let cashboxList: CashboxItem[] = sqlCashboxes.map((data: any) => ({
        id: data.id.toString(),
        name: data.name || '',
        description: data.description || '',
        currency: data.currency || 'TRY',
        balance: parseTurkishNumber(data.balance),
        createdAt: data.createdAt || '',
        isActive: data.isActive !== false,
        branchId: data.branchId || null,
        isDefault: data.isDefault || false,
        hasTransactions: data.hasTransactions || false
      }));
      
      cashboxList.sort((a, b) => {
        if (a.isDefault) return -1;
        if (b.isDefault) return 1;
        return a.name.localeCompare(b.name);
      });
      
      console.log('✅ [RELOAD] Kasalar yüklendi:', cashboxList.length, 'adet');
      console.log('💰 [RELOAD] Bakiyeler:', cashboxList.map(c => ({ name: c.name, balance: c.balance })));
      
      setCashboxes(cashboxList);
      setFilteredCashboxes(applyManagerFilter(cashboxList));
      
      // Özet bilgileri de güncelle
      if (!isManager) {
        loadCashboxSummary(companyId);
      }
    } catch (error) {
      console.error('❌ Kasalar yeniden yüklenirken hata:', error);
    }
  };

  const expenseCashboxes = useMemo<ExpenseCashboxOption[]>(
    () => applyManagerFilter(cashboxes)
      .filter(c => c.isActive !== false)
      .map(c => ({ id: c.id, name: c.name, currency: c.currency, balance: c.balance })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [cashboxes, isManager, managerBranchId]
  );

  if (isManager && canViewMovements && showManagerMovements) {
    return (
      <CashboxTransactions
        isManager
        managerBranchId={managerBranchId}
        onBack={() => setShowManagerMovements(false)}
      />
    );
  }

  return (
    <>
      {/* Üst Başlık ve Butonlar */}
      <HeaderContainer sx={{ flexDirection: { xs: 'column', md: 'row' }, alignItems: { xs: 'flex-start', md: 'center' }, gap: { xs: 2, md: 0 }, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', width: { xs: '100%', md: 'auto' } }}>
          <LocalAtmIcon sx={{ fontSize: { xs: 28, md: 32 }, mr: 1.5, color: 'primary.main' }} />
          <Typography variant={isMobile ? "h6" : "h5"} component="h1" fontWeight="bold">
            {t('cash.title')}
          </Typography>
        </Box>

        {/* Desktop Butonlar */}
        <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 1, alignItems: 'center' }}>
          <IconButton
            color="primary"
            onClick={() => setShowFilters(!showFilters)}
            sx={{ 
              bgcolor: 'primary.main', 
              color: 'white',
              '&:hover': { bgcolor: 'primary.dark' }
            }}
          >
            <FilterListIcon />
          </IconButton>
          {showExpenseButton && (
            <Button
              variant="contained"
              color="error"
              startIcon={<ReceiptLongIcon />}
              onClick={() => setIsExpenseModalOpen(true)}
            >
              {t('cash.actions.addExpense', { defaultValue: 'Gider Ekle' })}
            </Button>
          )}
          <ButtonGroup variant="contained">
            <Button 
              startIcon={<AddIcon />} 
              onClick={handleOpen}
            >
              {t('cash.actions.addCash')}
            </Button>
            <Button 
              onClick={handleOpenMoreMenu}
              sx={{
                minWidth: 'auto',
                px: 1
              }}
            >
              <MoreVertIcon />
            </Button>
          </ButtonGroup>
          <Menu
            anchorEl={anchorElMore}
            open={Boolean(anchorElMore)}
            onClose={handleCloseMoreMenu}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
          >
            {(!isManager || canViewMovements) && (
              <MenuItem onClick={handleOpenReports}>
                <ListItemIcon>
                  <AssessmentIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>{t('cash.actions.reports')}</ListItemText>
              </MenuItem>
            )}
            {!isManager && (
              <MenuItem onClick={() => { handleCloseMoreMenu(); setExpenseCategoriesOpen(true); }}>
                <ListItemIcon>
                  <ReceiptLongIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Gider Türleri</ListItemText>
              </MenuItem>
            )}
            <MenuItem onClick={() => { handleCloseMoreMenu(); exportToExcel(); }} disabled={loadingExport || loading}>
              <ListItemIcon>
                <FileDownloadIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>{loadingExport ? t('cash.actions.excelPreparing') : t('cash.actions.downloadExcel')}</ListItemText>
            </MenuItem>
          </Menu>
        </Box>

        {/* Mobil Butonlar */}
        <Grid container spacing={1} sx={{ display: { xs: 'flex', md: 'none' }, width: '100%' }}>
          {showExpenseButton && (
            <Grid item xs={12}>
              <Button
                variant="contained"
                color="error"
                size="small"
                fullWidth
                startIcon={<ReceiptLongIcon />}
                onClick={() => setIsExpenseModalOpen(true)}
              >
                {t('cash.actions.addExpense', { defaultValue: 'Gider Ekle' })}
              </Button>
            </Grid>
          )}
          <Grid item xs={6}>
            <Button 
              variant="contained" 
              size="small"
              fullWidth
              startIcon={<AddIcon />} 
              onClick={handleOpen}
            >
              {t('cash.actions.add')}
            </Button>
          </Grid>
          <Grid item xs="auto">
            <IconButton
              color="primary"
              onClick={handleOpenMoreMenu}
              sx={{ 
                bgcolor: 'primary.main', 
                color: 'white',
                '&:hover': { bgcolor: 'primary.dark' }
              }}
            >
              <MoreVertIcon />
            </IconButton>
          </Grid>
        </Grid>
      </HeaderContainer>

      {/* İstatistik Kartları (manager modunda şirket geneli özet gizli) */}
      {!isManager && (
      <StatsCardsContainer loading={summaryLoading}>
        {cashboxSummary && (
          <>
            <Grid item xs={12} sm={6} md={3}>
              <StatsCardItem
                icon={<AccountBalanceWalletIcon />}
                color={STATS_COLORS.blue}
                title={formatStatsCurrency(cashboxSummary.currentBalance)}
                subtitle={t('cash.stats.currentBalance')}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatsCardItem
                icon={<TrendingUpIcon />}
                color={STATS_COLORS.green}
                title={formatStatsCurrency(cashboxSummary.periodIncome)}
                subtitle={t('cash.stats.todayIncome')}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatsCardItem
                icon={<TrendingDownIcon />}
                color={STATS_COLORS.red}
                title={formatStatsCurrency(cashboxSummary.periodExpense)}
                subtitle={t('cash.stats.todayExpense')}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatsCardItem
                icon={<SwapHorizIcon />}
                color={cashboxSummary.periodNet >= 0 ? STATS_COLORS.green : STATS_COLORS.orange}
                title={formatStatsCurrency(cashboxSummary.periodNet)}
                subtitle={t('cash.stats.netDaily')}
              />
            </Grid>
          </>
        )}
      </StatsCardsContainer>
      )}

      {/* Arama Alanı (Filtreleme) */}
      {showFilters && (
        <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField
            size="small"
            placeholder={t('cash.searchPlaceholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 200, maxWidth: 300 }}
          />
          <Autocomplete
            size="small"
            options={branches}
            value={isManager ? (branches.find(b => b.id === managerBranchId) || null) : selectedBranchFilter}
            onChange={(_, newValue) => setSelectedBranchFilter(newValue)}
            getOptionLabel={(option) => option.name}
            disabled={isManager}
            renderInput={(params) => (
              <TextField 
                {...params} 
                placeholder={isManager ? t('current.filters.lockedBranch', { defaultValue: 'Şubeniz (Kilitli)' }) : t('cash.branchFilterPlaceholder')} 
                size="small"
              />
            )}
            sx={{ minWidth: 200 }}
          />
        </Box>
      )}

      {/* Ana İçerik */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      
      {loading ? (
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '400px' 
        }}>
          <CircularProgress />
        </Box>
      ) : filteredCashboxes.length === 0 ? (
        <Box sx={{ 
          p: 4, 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center',
          justifyContent: 'center',
          height: '400px',
          background: 'linear-gradient(135deg, #f5f7fa 0%, #e8ecf2 100%)',
          borderRadius: 2
        }}>
          <LocalAtmIcon 
            sx={{ 
              fontSize: 80, 
              color: 'primary.main',
              mb: 2
            }} 
          />
          
          <Typography variant="h5" component="h2" gutterBottom color="primary.main" fontWeight="bold">
            {t('cash.empty.title')}
          </Typography>
          
          <Typography variant="body1" color="text.secondary">
            {searchTerm ? t('cash.empty.search') : t('cash.empty.initial')}
          </Typography>
        </Box>
      ) : (
        <>
          {/* 📱 Mobile Card View (xs, sm) */}
          <Grid container spacing={2} sx={{ display: { xs: 'grid', md: 'none' }, gridTemplateColumns: '1fr', gap: 2, mb: 2 }}>
            {paginatedCashboxes.map((cashbox) => (
              <Card 
                key={cashbox.id}
                elevation={2} 
                sx={{ 
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: cashbox.isDefault ? 'primary.main' : 'divider',
                  bgcolor: cashbox.isDefault ? 'primary.50' : 'background.paper',
                  '&:hover': { 
                    boxShadow: 4,
                    borderColor: 'primary.main'
                  }
                }}
              >
                <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                  {/* Kasa Başlığı */}
                  <Box display="flex" alignItems="center" gap={1} mb={1.5}>
                    <AccountBalanceIcon sx={{ fontSize: 24, color: cashbox.isDefault ? 'primary.main' : 'action.active' }} />
                    <Box flex={1}>
                      <Typography variant="h6" fontWeight="bold" sx={{ fontSize: '1rem' }}>
                        {cashbox.name}
                      </Typography>
                      {cashbox.isDefault && (
                        <Chip label={t('cash.labels.default')} size="small" color="primary" sx={{ height: 20, fontSize: '0.7rem', mt: 0.5 }} />
                      )}
                    </Box>
                  </Box>

                  <Divider sx={{ mb: 1.5 }} />

                  {/* Açıklama */}
                  {cashbox.description && (
                    <Box display="flex" justifyContent="space-between" mb={1}>
                    <Typography variant="caption" color="text.secondary">{t('cash.labels.description')}:</Typography>
                      <Typography variant="caption" fontWeight="medium">{cashbox.description}</Typography>
                    </Box>
                  )}

                  {/* Para Birimi */}
                  <Box display="flex" justifyContent="space-between" mb={1}>
                    <Typography variant="caption" color="text.secondary">{t('cash.labels.currency')}:</Typography>
                    <Chip 
                      label={`${getCurrencySymbol(cashbox.currency)} ${cashbox.currency}`}
                      size="small"
                      variant="outlined"
                      sx={{ height: 20, fontSize: '0.7rem' }}
                    />
                  </Box>

                  {/* Bakiye */}
                  <Box display="flex" justifyContent="space-between" mb={1}>
                    <Typography variant="caption" color="text.secondary">{t('cash.labels.balance')}:</Typography>
                    <Typography 
                      variant="body2" 
                      fontWeight="bold" 
                      color={cashbox.balance >= 0 ? 'success.main' : 'error.main'}
                      sx={{ textAlign: 'right' }}
                    >
                      {cashbox.balance.toLocaleString(locale, { 
                        minimumFractionDigits: 2, 
                        maximumFractionDigits: 2 
                      })} {getCurrencySymbol(cashbox.currency)}
                    </Typography>
                  </Box>

                  {/* Şube */}
                  <Box display="flex" justifyContent="space-between" mb={1.5}>
                    <Typography variant="caption" color="text.secondary">{t('cash.labels.branch')}:</Typography>
                    <Typography variant="caption" fontWeight="medium">{getBranchName(cashbox.branchId)}</Typography>
                  </Box>

                  {!isManager && (
                    <>
                      <Divider sx={{ mb: 1.5 }} />

                      {/* İşlem Butonları */}
                      <Box display="flex" gap={1} justifyContent="flex-start">
                        <Button
                          fullWidth
                          size="small"
                          color="info"
                          variant="contained"
                          onClick={() => handleOpenTransactionsModal(cashbox)}
                        >
                          {t('cash.actions.process')}
                        </Button>
                        <IconButton
                          size="small"
                          onClick={(e) => handleOpenActionMenu(e, cashbox)}
                          sx={{ bgcolor: 'action.hover' }}
                        >
                          <MoreVertIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </>
                  )}
                </CardContent>
              </Card>
            ))}
          </Grid>

          {/* Desktop: AccountingDataTable */}
          <Box sx={{ display: { xs: 'none', md: 'block' } }}>
            <AccountingDataTable<CashboxItem>
              columns={cashboxColumns}
              rows={filteredCashboxes}
              getRowId={(row) => row.id}
              loading={loading}
              defaultRowsPerPage={25}
              defaultSortField="name"
              defaultSortDirection="asc"
              emptyMessage={t('cash.empty.noRecords')}
              emptyIcon={<LocalAtmIcon sx={{ fontSize: 48, color: 'text.disabled' }} />}
              ariaLabel={t('cash.tableAriaLabel')}
              columnResizeStorageKey="cashbox"
            />
          </Box>
        </>
      )}

      {/* Kasa İşlemler Menüsü (3 nokta) */}
      <Menu
        anchorEl={actionMenuAnchor}
        open={Boolean(actionMenuAnchor)}
        onClose={handleCloseActionMenu}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        {!isManager && (
          <MenuItem 
            onClick={handleMenuTransfer}
          >
            <ListItemIcon>
              <SwapHorizIcon fontSize="small" color="info" />
            </ListItemIcon>
            <ListItemText>{t('cash.actions.cashTransfer')}</ListItemText>
          </MenuItem>
        )}
        {!isManager && (
          <MenuItem 
            onClick={handleMenuBankTransfer}
          >
            <ListItemIcon>
              <AccountBalanceIcon fontSize="small" color="success" />
            </ListItemIcon>
            <ListItemText>{t('cash.actions.bankTransfer')}</ListItemText>
          </MenuItem>
        )}
        {!isManager && (
          <MenuItem 
            onClick={handleMenuEdit}
            disabled={selectedCashboxForMenu?.isDefault || selectedCashboxForMenu?.hasTransactions}
          >
            <ListItemIcon>
              <EditIcon fontSize="small" color="primary" />
            </ListItemIcon>
            <ListItemText>
              {t('cash.actions.edit')}
              {selectedCashboxForMenu?.isDefault && ` (${t('cash.labels.defaultCashSuffix')})`}
              {selectedCashboxForMenu?.hasTransactions && ` (${t('cash.labels.hasTransactionSuffix')})`}
            </ListItemText>
          </MenuItem>
        )}
        {!isManager && (
          <MenuItem 
            onClick={handleMenuDelete}
            disabled={selectedCashboxForMenu?.isDefault || selectedCashboxForMenu?.hasTransactions}
          >
            <ListItemIcon>
              <DeleteIcon fontSize="small" color="error" />
            </ListItemIcon>
            <ListItemText>
              {t('cash.actions.delete')}
              {selectedCashboxForMenu?.isDefault && ` (${t('cash.labels.defaultCashSuffix')})`}
              {selectedCashboxForMenu?.hasTransactions && ` (${t('cash.labels.hasTransactionSuffix')})`}
            </ListItemText>
          </MenuItem>
        )}
      </Menu>

      {/* Kasa Ekle Modal */}
      <Modal 
        open={open} 
        onClose={!saving ? handleClose : undefined}
        aria-labelledby="kasa-ekle-modal"
      >
        <Paper 
          sx={{ 
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: { xs: '90%', sm: 500 },
            bgcolor: 'background.paper',
            boxShadow: 24,
            p: 4,
            borderRadius: 2
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" component="h2" fontWeight="bold">
              {t('cash.modal.addTitle')}
            </Typography>
            <IconButton onClick={handleClose} size="small" disabled={saving}>
              <CloseIcon />
            </IconButton>
          </Box>

          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                autoFocus
                margin="dense"
                id="kasaAdi"
                label={t('cash.modal.fields.cashName')}
                type="text"
                fullWidth
                variant="outlined"
                value={kasaAdi}
                onChange={(e) => setKasaAdi(e.target.value)}
                required
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth margin="dense">
                <InputLabel id="currency-label">{t('cash.modal.fields.currency')}</InputLabel>
                <Select
                  labelId="currency-label"
                  id="currency"
                  value={selectedCurrency}
                  label={t('cash.modal.fields.currency')}
                  onChange={handleCurrencyChange}
                >
                  {currencies.map((currency) => (
                    <MenuItem key={currency} value={currency}>
                      {getCurrencySymbol(currency)} {currency}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <Autocomplete
                id="branch-select"
                options={branches}
                value={selectedBranch}
                onChange={handleBranchChange}
                getOptionLabel={(option) => option.name}
                disabled={isManager}
                renderInput={(params) => (
                  <TextField 
                    {...params} 
                    label={isManager ? t('current.filters.lockedBranch', { defaultValue: 'Şube (kilitli)' }) : t('cash.modal.fields.branchOptional')} 
                    margin="dense"
                  />
                )}
                fullWidth
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                margin="dense"
                id="aciklama"
                label={t('cash.modal.fields.description')}
                type="text"
                fullWidth
                multiline
                rows={3}
                variant="outlined"
                value={aciklama}
                onChange={(e) => setAciklama(e.target.value)}
              />
            </Grid>
          </Grid>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3, gap: 2 }}>
            <Button 
              variant="outlined" 
              onClick={handleClose}
              disabled={saving}
            >
              {t('cash.common.cancel')}
            </Button>
            <Button 
              variant="contained" 
              onClick={handleSave}
              disabled={!kasaAdi.trim() || saving}
              startIcon={saving ? <CircularProgress size={24} /> : null}
            >
              {saving ? t('cash.common.saving') : t('cash.common.save')}
            </Button>
          </Box>
        </Paper>
      </Modal>

      {/* Kasa Düzenle Modal */}
      <Modal 
        open={isEditModalOpen} 
        onClose={!updating ? handleCloseEditModal : undefined}
        aria-labelledby="kasa-duzenle-modal"
      >
        <Paper 
          sx={{ 
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: { xs: '90%', sm: 500 },
            bgcolor: 'background.paper',
            boxShadow: 24,
            p: 4,
            borderRadius: 2
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" component="h2" fontWeight="bold">
              {t('cash.modal.editTitle')}
            </Typography>
            <IconButton onClick={handleCloseEditModal} size="small" disabled={updating}>
              <CloseIcon />
            </IconButton>
          </Box>

          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                autoFocus
                margin="dense"
                id="editName"
                label={t('cash.modal.fields.cashName')}
                type="text"
                fullWidth
                variant="outlined"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                required
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth margin="dense">
                <InputLabel id="edit-currency-label">{t('cash.modal.fields.currency')}</InputLabel>
                <Select
                  labelId="edit-currency-label"
                  id="editCurrency"
                  value={editCurrency}
                  label={t('cash.modal.fields.currency')}
                  onChange={(e) => setEditCurrency(e.target.value)}
                >
                  {currencies.map((currency) => (
                    <MenuItem key={currency} value={currency}>
                      {currency}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <Autocomplete
                id="edit-branch-select"
                options={branches}
                value={editBranch}
                onChange={(_, newValue) => setEditBranch(newValue)}
                getOptionLabel={(option) => option.name}
                disabled={isManager}
                renderInput={(params) => (
                  <TextField 
                    {...params} 
                    label={isManager ? t('current.filters.lockedBranch', { defaultValue: 'Şube (kilitli)' }) : t('cash.modal.fields.branchOptional')} 
                    margin="dense"
                  />
                )}
                fullWidth
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                margin="dense"
                id="editDescription"
                label={t('cash.modal.fields.description')}
                type="text"
                fullWidth
                multiline
                rows={3}
                variant="outlined"
                value={editDescription}
                onChange={(e) => setEditDescription(e.target.value)}
              />
            </Grid>
          </Grid>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3, gap: 2 }}>
            <Button 
              variant="outlined" 
              onClick={handleCloseEditModal}
              disabled={updating}
            >
              {t('cash.common.cancel')}
            </Button>
            <Button 
              variant="contained" 
              onClick={handleUpdateCashbox}
              disabled={!editName.trim() || updating}
              startIcon={updating ? <CircularProgress size={24} /> : null}
            >
              {updating ? t('cash.common.updating') : t('cash.common.update')}
            </Button>
          </Box>
        </Paper>
      </Modal>

      {/* Kasa Silme Onay Modal */}
      <Modal
        open={isDeleteModalOpen}
        onClose={!deleting ? handleCloseDeleteModal : undefined}
        aria-labelledby="kasa-sil-modal"
      >
        <Paper
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: { xs: '90%', sm: 450 },
            bgcolor: 'background.paper',
            boxShadow: 24,
            p: 4,
            borderRadius: 2
          }}
        >
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" component="h2" fontWeight="bold" color="error" gutterBottom>
              {t('cash.deleteModal.title')}
            </Typography>
            <Typography variant="body1">
              {t('cash.deleteModal.confirm', { name: selectedCashbox?.name })}
            </Typography>
            <Typography variant="body2" color="error" sx={{ mt: 2, fontWeight: 'bold' }}>
              {t('cash.deleteModal.warning')}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
            <Button
              variant="outlined"
              onClick={handleCloseDeleteModal}
              disabled={deleting}
            >
              {t('cash.common.cancel')}
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={handleDeleteCashbox}
              disabled={deleting}
              startIcon={deleting ? <CircularProgress size={24} color="inherit" /> : <DeleteIcon />}
            >
              {deleting ? t('cash.common.deleting') : t('cash.deleteModal.confirmButton')}
            </Button>
          </Box>
        </Paper>
      </Modal>

      {/* Hareketler Modalı */}
      <Modal
        open={isTransactionsModalOpen}
        onClose={handleCloseTransactionsModal}
        aria-labelledby="kasa-hareketleri-modal"
      >
        <Paper
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: { xs: '90%', sm: 500 },
            bgcolor: 'background.paper',
            boxShadow: 24,
            p: 4,
            borderRadius: 2
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6" component="h2" fontWeight="bold">
              {transactionCashbox?.name}
            </Typography>
            <IconButton onClick={handleCloseTransactionsModal} size="small">
              <CloseIcon />
            </IconButton>
          </Box>

          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="body1" gutterBottom>
                {t('cash.labels.balance')}: <Typography 
                  component="span" 
                  fontWeight="bold"
                  color={transactionCashbox && transactionCashbox.balance >= 0 ? 'success.main' : 'error.main'}
                >
                  {transactionCashbox?.balance.toLocaleString(locale, { 
                    minimumFractionDigits: 2, 
                    maximumFractionDigits: 2 
                  })} {transactionCashbox ? getCurrencySymbol(transactionCashbox.currency) : ''}
                </Typography>
              </Typography>
            </Grid>
            
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                <Button 
                  variant="outlined" 
                  color="primary" 
                  fullWidth
                  startIcon={<ArrowUpwardIcon />}
                  size="large"
                  sx={{ py: 1.5, flex: 1 }}
                  onClick={() => handleOpenDepositModal(transactionCashbox!)}
                >
                  {t('cash.actions.deposit')}
                </Button>
                <Button 
                  variant="outlined" 
                  color="primary" 
                  fullWidth
                  startIcon={<ArrowDownwardIcon />}
                  size="large"
                  sx={{ py: 1.5, flex: 1 }}
                  onClick={() => handleOpenWithdrawModal(transactionCashbox!)}
                >
                  {t('cash.actions.withdraw')}
                </Button>
              </Box>

              <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                <Button 
                  variant="outlined" 
                  color="info" 
                  fullWidth
                  startIcon={<SwapHorizIcon />}
                  size="large"
                  sx={{ py: 1.5, flex: 1 }}
                  onClick={() => {
                    if (transactionCashbox) {
                      setTransferSourceCashbox(transactionCashbox);
                      setIsCashboxTransferModalOpen(true);
                      setIsTransactionsModalOpen(false);
                    }
                  }}
                >
                  {t('cash.actions.cashTransfer')}
                </Button>
                <Button 
                  variant="outlined" 
                  color="success" 
                  fullWidth
                  startIcon={<AccountBalanceIcon />}
                  size="large"
                  sx={{ py: 1.5, flex: 1 }}
                  onClick={() => {
                    if (transactionCashbox) {
                      setBankTransferSourceCashbox(transactionCashbox);
                      setIsBankTransferModalOpen(true);
                      setIsTransactionsModalOpen(false);
                    }
                  }}
                >
                  {t('cash.actions.bankTransfer')}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Paper>
      </Modal>

      {/* Para Yatırma Modalı */}
      {depositCashbox && (
        <DepositModal
          open={isDepositModalOpen}
          onClose={handleCloseDepositModal}
          cashboxId={depositCashbox.id}
          cashboxName={depositCashbox.name}
          currency={depositCashbox.currency}
          companyId={companyId || ''}
          currencySymbol={getCurrencySymbol(depositCashbox.currency)}
          balance={depositCashbox.balance}
          onSuccess={() => {
            // Yeni işlem yapıldıktan sonra hareketler sayfasını yenileme ihtiyacı olabilir
            setError(null);
          }}
        />
      )}

      {/* Para Çekme Modalı */}
      {withdrawCashbox && (
        <WithdrawModal
          open={isWithdrawModalOpen}
          onClose={handleCloseWithdrawModal}
          cashboxId={withdrawCashbox.id}
          cashboxName={withdrawCashbox.name}
          currency={withdrawCashbox.currency}
          companyId={companyId || ''}
          currencySymbol={getCurrencySymbol(withdrawCashbox.currency)}
          balance={withdrawCashbox.balance}
          onSuccess={() => {
            // Yeni işlem yapıldıktan sonra hareketler sayfasını yenileme ihtiyacı olabilir
            setError(null);
          }}
        />
      )}

      {/* Para Transferi Modalı */}
      {companyId && (
        <CashTransferModal
          open={isTransferModalOpen}
          onClose={handleCloseTransferModal}
          companyId={companyId}
          onSuccess={() => {
            setError(null);
          }}
        />
      )}

      {/* Kasa Transferi Modalı (Yeni - Kasadan Kasaya) */}
      {transferSourceCashbox && companyId && (
        <CashboxTransferModal
          open={isCashboxTransferModalOpen}
          onClose={handleCloseCashboxTransferModal}
          sourceCashbox={transferSourceCashbox}
          companyId={companyId}
          onSuccess={() => {
            setError(null);
          }}
        />
      )}

      {/* Banka Transferi Modalı (Yeni - Kasadan Bankaya) */}
      {bankTransferSourceCashbox && companyId && (
        <CashboxBankTransferModal
          open={isBankTransferModalOpen}
          onClose={handleCloseBankTransferModal}
          sourceCashbox={bankTransferSourceCashbox}
          companyId={companyId}
          onSuccess={() => {
            setError(null);
            // Kasaları yeniden yükle
            reloadCashboxes();
          }}
        />
      )}

      {showExpenseButton && (
        <CashExpenseModal
          open={isExpenseModalOpen}
          onClose={() => setIsExpenseModalOpen(false)}
          cashboxes={expenseCashboxes}
          allowDate={!isManager}
          onSuccess={() => {
            setError(null);
            reloadCashboxes();
          }}
        />
      )}

      {!isManager && (
        <CashExpenseCategoriesModal
          open={expenseCategoriesOpen}
          onClose={() => setExpenseCategoriesOpen(false)}
        />
      )}
    </>
  );
};

export default Cashbox; 